<template>
  <DropdownMenuRoot
    v-if="channelStore.permissions.manageOffers"
    v-model:open="isOpen"
  >
    <div
      class="size-8 bg-fill-brand-secondary/20 rounded-full backdrop-blur-[35px] flex items-center justify-center"
    >
      <DropdownMenuTrigger
        :aria-label="$t('channel.offers.card.dropdown.aria')"
      >
        <Icon
          icon="ph:dots-three"
          class="text-icon-secondary"
          width="24"
          height="24"
        />
      </DropdownMenuTrigger>
    </div>

    <DropdownMenuPortal>
      <DropdownMenuContent
        class="outline-none shadow bg-surface-secondary/90 text-text-secondary backdrop-blur-3xl p-2 rounded-xl will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade z-[999999999]"
        :side-offset="5"
      >
        <DropdownMenuItem
          class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand text-text-primary data-[highlighted]:text-text-primary rounded-md px-2 py-1 gap-2 cursor-pointer"
          @click="onEditClick"
        >
          <Icon icon="material-symbols:edit" width="24" height="24" />
          {{ $t("channel.offers.card.dropdown.edit") }}
        </DropdownMenuItem>

        <DropdownMenuItem
          class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand data-[highlighted]:text-text-primary text-red-400 rounded-md px-2 py-1 gap-2 cursor-pointer"
          @click="deleteConfirmation = true"
        >
          <Icon icon="ph:trash" width="24" height="24" />
          {{ $t("channel.offers.card.dropdown.delete") }}
        </DropdownMenuItem>

        <DropdownMenuArrow
          class="fill-surface-secondary/90 backdrop-blur-3xl"
        />
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>

  <DangerConfirmationModal
    v-model="deleteConfirmation"
    :title="$t('channel.offers.card.dropdown.deleteConfirmTitle')"
    :confirm-label="$t('channel.offers.card.dropdown.deleteConfirmLabel')"
    @confirm="onDelete"
  />
</template>

<script setup lang="ts">
import { ref, defineAsyncComponent } from "vue";
import { Icon } from "@iconify/vue/dist/iconify.js";
import { useRouter } from "vue-router";
import { useMutation } from "@urql/vue";
import {
  DropdownMenuArrow,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from "reka-ui";
import { DeleteChannelOfferFromPageDocument } from "@/generated/graphql";
import { RouteNames } from "@/router/app.router";
import { useChannelStore } from "@/stores/channel.store";

// Lazy load
const DangerConfirmationModal = defineAsyncComponent(
  () => import("@/components/show/modals/danger-confirmation-modal.vue"),
);

const props = defineProps<{
  offerId: string;
}>();

const emit = defineEmits<{
  deleted: [];
}>();

const router = useRouter();
const channelStore = useChannelStore();

const isOpen = ref(false);
const deleteConfirmation = ref(false);

const { executeMutation: deleteOffer } = useMutation(
  DeleteChannelOfferFromPageDocument,
);

function onEditClick() {
  router.push({
    name: RouteNames.EditOffer,
    params: { channel: channelStore.channelSlug, offerId: props.offerId },
  });
}

async function onDelete() {
  const result = await deleteOffer({ id: props.offerId });
  if (result.data?.deleteChannelOffer.__typename === "Offer") {
    emit("deleted");
  }
}
</script>
